/**
 * Customer detail — S10. Inline-edit detail page; the `/edit`
 * route is retired (redirects here).
 *
 * Composition:
 *   1. Cover hero — avatar, name, pills, actions (`Edit ▾`
 *      dropdown + Send reminder + New invoice + Archive). 3-cell
 *      info grid (Contact / Address / GST & PAN) with per-cell
 *      pencil edits + empty-state inline-add affordances.
 *   2. CompletionStrip — at-a-glance progress over editable
 *      sections; dismissible.
 *   3. Stat strip — Outstanding / YTD / Avg payment time / Credit.
 *   4. Tabs — Overview · Invoices · Activity · Notes. The Overview
 *      tab renders the 2-column body (Outstanding/Recent invoices/
 *      Activity timeline + right-rail Payment health / Credit
 *      limit / Communication / Pinned note / Quick actions).
 *
 * The 7 edit dialogs (Identity, Contact, Address, Tax, Terms,
 * Communication, Tags/Notes) are mounted once at this level and
 * triggered from either the cover cells/rows or the top-right Edit
 * dropdown.
 */
import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, ConfirmDialog, TabsRoot, TabsList, TabsTrigger, TabsContent, toast } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { firstZodIssueMessage } from "../../../../lib/trpc-errors.ts";
import {
  buildCustomerDetailModel,
  CustomerActivityTab,
  CustomerCover,
  CustomerDetailEmptyTab,
  CustomerDetailOverview,
  CustomerInvoicesList,
  DetailStatStrip,
} from "../../../../features/customers/index.ts";
import { exportCustomerStatementXlsx } from "../../../../features/customers/ledger/index.ts";
import { CompletionStrip } from "../../../../features/customers/components/completion-strip.tsx";
import {
  EditIdentityDialog,
  EditContactDialog,
  EditAddressDialog,
  EditTaxDialog,
  EditTermsDialog,
  EditTagsAndNotesDialog,
  EditCommunicationDialog,
} from "../../../../features/customers/edit-dialogs/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import "../../../../features/customers/components/customer-detail.css";

type EditTarget = "identity" | "contact" | "address" | "tax" | "terms" | "communication" | "tags";

const completionDismissedKey = (id: string) => `flint.customer.${id}.completion-dismissed`;

export const Route = createFileRoute("/(workspace)/sales/customers/$customerId/")({
  component: CustomerDetailPage,
});

function CustomerDetailPage() {
  const { customerId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const customer = trpc.customers.get.useQuery({ id: customerId });
  const invoices = trpc.invoices.list.useQuery(
    { customerId, limit: 100 },
    { enabled: customer.data !== undefined },
  );
  const ledger = trpc.customers.ledger.useQuery(
    { id: customerId },
    { enabled: customer.data !== undefined },
  );
  const activity = trpc.customers.activity.useInfiniteQuery(
    { id: customerId, limit: 10 },
    {
      enabled: customer.data !== undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
    },
  );
  const activityRows = useMemo(
    () => activity.data?.pages.flatMap((p) => p.items) ?? [],
    [activity.data],
  );
  const model = useMemo(
    () =>
      customer.data ? buildCustomerDetailModel(customer.data, invoices.data, activityRows) : null,
    [customer.data, invoices.data, activityRows],
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [stripDismissed, setStripDismissed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(completionDismissedKey(customerId)) === "1";
  });
  const [tab, setTab] = useState<"overview" | "invoices" | "activity" | "notes">("overview");

  const archive = trpc.customers.archive.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.customers.list.invalidate(),
        utils.customers.counts.invalidate(),
        utils.customers.get.invalidate({ id: customerId }),
      ]);
      void navigate({ to: "/sales/customers", search: {} });
    },
  });

  const updateCustomer = trpc.customers.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.customers.get.invalidate({ id: customerId }),
        utils.customers.activity.invalidate({ id: customerId }),
      ]);
    },
  });

  // Cover + right-rail "Send WhatsApp reminder" — fires
  // `invoices.send` for this customer's most-overdue open invoice
  // (`model.openInvoice`). Both buttons share the same mutation
  // instance so `Sending…` / `Reminder sent` flash in unison, and a
  // Sonner toast surfaces the confirmation (or the error) so the
  // operator never has to guess whether the click worked.
  const [reminderJustSent, setReminderJustSent] = useState(false);
  const sendReminder = trpc.invoices.send.useMutation({
    onSuccess: async (_result, variables) => {
      await Promise.all([
        utils.customers.activity.invalidate({ id: customerId }),
        utils.invoices.list.invalidate({ customerId, limit: 100 }),
      ]);
      setReminderJustSent(true);
      window.setTimeout(() => setReminderJustSent(false), 4000);
      const inv = invoices.data?.items.find((i) => i.id === variables.invoiceId);
      const docLabel = inv?.docNumber?.trim() || "the open invoice";
      toast.success(`Reminder sent to ${customer.data?.name ?? "customer"}`, {
        description: `WhatsApp reminder queued for ${docLabel}.`,
        duration: 6000,
      });
    },
    onError: (err) => {
      toast.error("Couldn't send reminder", {
        description: err.message,
        duration: 8000,
      });
    },
  });

  const savePatch = async (patch: Record<string, unknown>) => {
    await updateCustomer.mutateAsync({ id: customerId, ...patch });
    setEditTarget(null);
  };

  if (customer.isLoading) {
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Sales", to: "/" },
          { label: "Customers", to: "/sales/customers" },
          { label: "Loading…" },
        ]}
      >
        <p className="text-muted">Loading customer…</p>
      </WorkspaceShell>
    );
  }
  if (customer.error) {
    const isNotFound = customer.error.data?.code === "NOT_FOUND";
    return (
      <WorkspaceShell
        breadcrumbs={[
          { label: "Sales", to: "/" },
          { label: "Customers", to: "/sales/customers" },
          { label: "Customer" },
        ]}
      >
        <Alert
          intent="bad"
          title={isNotFound ? "Customer not found." : "Couldn't load customer."}
          description={customer.error.message}
        />
      </WorkspaceShell>
    );
  }
  if (!customer.data) return null;
  const data = customer.data;
  if (!model) return null;

  // Unwrap Zod validation errors so the user sees a readable sentence
  // (e.g. "invalid GSTIN") instead of the raw `[{"code":…}]` array —
  // see AC29 in docs/qa/test-cases/15-customers-detail.md. Non-Zod errors
  // (locked field, RLS, network) fall through to `error.message`.
  const updateState = {
    pending: updateCustomer.isPending,
    serverError: updateCustomer.error
      ? firstZodIssueMessage(updateCustomer.error) ?? updateCustomer.error.message
      : null,
  };

  const editActions = {
    onEditIdentity: () => setEditTarget("identity"),
    onEditContact: () => setEditTarget("contact"),
    onEditAddress: () => setEditTarget("address"),
    onEditTax: () => setEditTarget("tax"),
    onEditTerms: () => setEditTarget("terms"),
    onEditCommunication: () => setEditTarget("communication"),
    onEditTags: () => setEditTarget("tags"),
  };

  // Reminder button guards. Order matches the button's tooltip
  // priority: no-open-invoice is the more informative message when
  // both apply (an archived-but-past-due invoice still counts as open).
  const reminderDisabledReason: string | null = !model.openInvoice
    ? "No open invoice to remind about"
    : !data.phone
      ? "No phone on file for WhatsApp"
      : null;

  const handleSendReminder = () => {
    if (reminderDisabledReason || !model.openInvoice) return;
    sendReminder.mutate({ invoiceId: model.openInvoice.id, channel: "whatsapp" });
  };

  // Export-statement guard + handler. Ledger is a read-only projection
  // over invoices + receipts + credit_notes; if the customer has zero
  // rows the CSV would be a header-only document, so disable + tooltip.
  const exportDisabledReason: string | null = ledger.isLoading
    ? "Loading ledger…"
    : !ledger.data || ledger.data.rows.length === 0
      ? "No ledger activity to export yet"
      : null;

  const handleExportStatement = () => {
    if (exportDisabledReason || !ledger.data) return;
    void exportCustomerStatementXlsx(data, ledger.data);
    toast.success(`Statement downloaded for ${data.name}`);
  };

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Sales", to: "/" },
        { label: "Customers", to: "/sales/customers" },
        { label: data.name },
      ]}
    >
      <div className="customer-detail-page">
        <CustomerCover
          customer={data}
          summary={model.summary}
          archiving={archive.isPending}
          onArchive={() => setConfirmOpen(true)}
          editActions={editActions}
          onSendReminder={handleSendReminder}
          reminderPending={sendReminder.isPending}
          reminderJustSent={reminderJustSent}
          reminderDisabledReason={reminderDisabledReason}
        />

        {archive.error ? (
          <Alert intent="bad" title="Archive failed" description={archive.error.message} />
        ) : null}

        {/*
         * Hidden live-region for the WhatsApp reminder — Sonner covers the
         * visible toast for sighted operators; this satisfies screen-reader
         * announcement + the AC16 automated assertion (which queries
         * `role="status"` with reminder text) without duplicating the visual.
         */}
        {reminderJustSent && model.openInvoice ? (
          <div role="status" aria-live="polite" className="sr-only">
            Reminder sent to {data.name} for{" "}
            {model.openInvoice.docNumber || "the open invoice"}.
          </div>
        ) : null}

        {stripDismissed ? null : (
          <CompletionStrip
            customer={data}
            actions={{
              onEditContact: editActions.onEditContact,
              onEditTax: editActions.onEditTax,
              onEditTerms: editActions.onEditTerms,
              onSkipAll: () => {
                window.localStorage.setItem(completionDismissedKey(customerId), "1");
                setStripDismissed(true);
              },
            }}
          />
        )}

        <DetailStatStrip metrics={model.metrics} />

        <TabsRoot
          value={tab}
          onValueChange={(v) => setTab(v as typeof tab)}
          className="customer-detail-tabs-root"
        >
          <TabsList aria-label="Customer sections" className="customer-detail-tabs">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="invoices" count={model.invoiceCount}>
              Invoices
            </TabsTrigger>
            <TabsTrigger value="activity" count={model.activityCount}>
              Activity
            </TabsTrigger>
            <TabsTrigger value="notes" count={0}>
              Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="customer-detail-tab-panel">
            <CustomerDetailOverview
              customer={data}
              model={model}
              activityFetch={{
                hasMore: Boolean(activity.hasNextPage),
                isFetching: activity.isFetchingNextPage,
                onLoadMore: () => {
                  void activity.fetchNextPage();
                },
              }}
              editActions={{
                onEditTax: editActions.onEditTax,
                onEditTerms: editActions.onEditTerms,
                onEditCommunication: editActions.onEditCommunication,
                onEditTags: editActions.onEditTags,
              }}
              onViewAllInvoices={() =>
                void navigate({ to: "/sales/invoices", search: { customerId } })
              }
              onOpenLedger={() =>
                void navigate({ to: "/sales/customers/$customerId/ledger", params: { customerId } })
              }
              onSendReminder={handleSendReminder}
              reminderPending={sendReminder.isPending}
              reminderJustSent={reminderJustSent}
              reminderDisabledReason={reminderDisabledReason}
              onExportStatement={handleExportStatement}
              exportDisabledReason={exportDisabledReason}
            />
          </TabsContent>

          <TabsContent value="invoices" className="customer-detail-tab-panel">
            <CustomerInvoicesList items={invoices.data?.items ?? []} />
          </TabsContent>

          <TabsContent value="activity" className="customer-detail-tab-panel">
            <CustomerActivityTab
              model={model}
              customerName={data.name}
              activityFetch={{
                hasMore: Boolean(activity.hasNextPage),
                isFetching: activity.isFetchingNextPage,
                onLoadMore: () => {
                  void activity.fetchNextPage();
                },
              }}
            />
          </TabsContent>

          <TabsContent value="notes" className="customer-detail-tab-panel">
            <CustomerDetailEmptyTab
              title="Internal notes"
              description="No notes API exists yet, so this tab intentionally renders empty instead of showing placeholder notes."
            />
          </TabsContent>
        </TabsRoot>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Archive ${data.name}?`}
        description="Archived customers stay in your records and the audit trail. They're hidden from the active list and the dropdowns on new invoices. You can browse archived customers from the “Archived” tab."
        confirmLabel="Archive customer"
        cancelLabel="Cancel"
        intent="danger"
        pending={archive.isPending}
        onConfirm={async () => {
          await archive.mutateAsync({ id: customerId });
        }}
      />

      <EditIdentityDialog
        open={editTarget === "identity"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditContactDialog
        open={editTarget === "contact"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditAddressDialog
        open={editTarget === "address"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditTaxDialog
        open={editTarget === "tax"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditTermsDialog
        open={editTarget === "terms"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditCommunicationDialog
        open={editTarget === "communication"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
      <EditTagsAndNotesDialog
        open={editTarget === "tags"}
        onOpenChange={(o) => !o && setEditTarget(null)}
        customer={data}
        {...updateState}
        onSave={savePatch}
      />
    </WorkspaceShell>
  );
}
